import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  MessageCircle,
  TrendingDown,
  Clock,
} from "lucide-react";
import { aiUstozAPI } from "../../api";
import { useLang } from "../../context/LangContext";
import type { InsightPayload } from "./types";

type LoadState = "loading" | "ready" | "empty" | "error";

const MODE_LABELS: Record<string, string> = {
  topic: "Mavzu",
  ticket: "Bilet",
  exam: "Nazorat",
  random: "Tasodifiy",
};

const InsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [state, setState] = useState<LoadState>("loading");
  const [data, setData] = useState<InsightPayload | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const load = useCallback(() => {
    setState("loading");
    aiUstozAPI
      .getInsights(lang)
      .then((res) => {
        setData(res.data);
        setState("ready");
      })
      .catch((e: any) => {
        const status = e?.response?.status;
        if (status === 404) {
          setState("empty");
        } else {
          setErrorMsg(
            e?.response?.data?.message ||
              "AI xizmati vaqtincha ishlamayapti, keyinroq urinib ko'ring",
          );
          setState("error");
        }
      });
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* Fixed header */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: "calc(52px + env(safe-area-inset-top))",
          paddingTop: "env(safe-area-inset-top)",
        }}>
        <div
          style={{
            maxWidth: 560,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--muted)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                padding: 0,
              }}>
              <ArrowLeft size={16} /> Orqaga
            </button>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              Ustoz AI
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {state === "ready" && (
              <button
                onClick={load}
                title="Yangilash"
                style={{
                  background: "transparent",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  color: "var(--muted)",
                  cursor: "pointer",
                  padding: 7,
                  display: "flex",
                }}>
                <RefreshCw size={14} />
              </button>
            )}
            <button
              onClick={() => navigate("/ai-ustoz/chat")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: 10,
                background: "#3B82F6",
                border: "none",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}>
              <MessageCircle size={13} /> Chat
            </button>
          </div>
        </div>
      </div>

      <div
        style={{ maxWidth: 560, margin: "0 auto", padding: "68px 16px 32px" }}>
        {state === "loading" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              paddingTop: 100,
            }}>
            <Loader2 className="animate-spin text-blue-500" size={32} />
            <p style={{ color: "var(--muted)", fontSize: 13 }}>
              Ustoz AI statistikangizni tahlil qilmoqda...
            </p>
          </div>
        )}

        {state === "empty" && (
          <div
            className="fade-up"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 80,
              textAlign: "center",
              gap: 20,
            }}>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 24,
                background: "rgba(59,130,246,.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Sparkles size={32} style={{ color: "#3B82F6" }} />
            </div>
            <div>
              <p
                style={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 18,
                  marginBottom: 8,
                }}>
                Hali tahlil uchun ma'lumot yo'q
              </p>
              <p
                style={{
                  color: "var(--muted)",
                  fontSize: 14,
                  lineHeight: 1.5,
                  maxWidth: 280,
                }}>
                Hali test yechmagansiz. Ustoz AI sizga tavsiya berishi uchun
                kamida bitta testni yakunlang.
              </p>
            </div>
            <button
              onClick={() => navigate("/tests")}
              style={{
                marginTop: 10,
                padding: "12px 24px",
                borderRadius: 12,
                background: "#3B82F6",
                border: "none",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}>
              Test boshlash
            </button>
          </div>
        )}

        {state === "error" && (
          <div
            className="fade-up"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 80,
              textAlign: "center",
              gap: 16,
            }}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 20,
                background: "rgba(239,68,68,.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <AlertTriangle size={28} style={{ color: "#EF4444" }} />
            </div>
            <p style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              {errorMsg}
            </p>
            <button
              onClick={load}
              style={{
                padding: "10px 20px",
                borderRadius: 12,
                background: "var(--card)",
                border: "1px solid var(--border)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
              <RefreshCw size={14} /> Qayta urinish
            </button>
          </div>
        )}

        {state === "ready" && data && (
          <div className="fade-up">
            {/* AI xulosa */}
            <div
              style={{
                background:
                  "linear-gradient(135deg, rgba(59,130,246,.12), rgba(59,130,246,.03))",
                border: "1px solid rgba(59,130,246,.25)",
                borderRadius: 16,
                padding: 16,
                marginBottom: 16,
              }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 9,
                    background: "#3B82F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  <Sparkles size={14} style={{ color: "#fff" }} />
                </div>
                <span style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>
                  Ustoz AI dedi:
                </span>
              </div>
              <p
                style={{
                  color: "#E2E8F0",
                  fontSize: 14,
                  lineHeight: 1.6,
                  marginBottom: 10,
                }}>
                {data.summary}
              </p>
              <div
                style={{
                  background: "rgba(255,255,255,.04)",
                  borderRadius: 10,
                  padding: "10px 12px",
                }}>
                <p
                  style={{
                    color: "#93C5FD",
                    fontSize: 13,
                    lineHeight: 1.5,
                    fontWeight: 600,
                  }}>
                  💡 {data.recommendation}
                </p>
              </div>
            </div>

            {/* Umumiy statistika */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 10,
                marginBottom: 16,
              }}>
              {[
                {
                  label: "Javob berilgan",
                  value: data.totalAnswered,
                  color: "#3B82F6",
                },
                {
                  label: "Umumiy aniqlik",
                  value: `${data.overallAccuracy}%`,
                  color: "#22C55E",
                },
                {
                  label: "O'rtacha ball",
                  value: `${Math.round(data.progress.summary.avgScore)}%`,
                  color: "#F59E0B",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    borderRadius: 14,
                    padding: "14px 10px",
                    textAlign: "center",
                  }}>
                  <p style={{ color: s.color, fontWeight: 800, fontSize: 18 }}>
                    {s.value}
                  </p>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 10,
                      fontWeight: 600,
                      marginTop: 4,
                    }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Zaif mavzular */}
            {data.weakTopics.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                  }}>
                  <TrendingDown size={14} style={{ color: "#EF4444" }} />
                  <span
                    style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    Zaif mavzular
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.weakTopics.map((wt) => (
                    <button
                      key={wt.topicId}
                      onClick={() => navigate(`/quiz/topic/${wt.topicId}`)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: "12px 14px",
                        borderRadius: 12,
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}>
                      <div>
                        <p
                          style={{
                            color: "#E2E8F0",
                            fontSize: 13,
                            fontWeight: 600,
                          }}>
                          {wt.title}
                        </p>
                        <p
                          style={{
                            color: "var(--muted)",
                            fontSize: 11,
                            marginTop: 2,
                          }}>
                          {wt.totalMistakes} marta xato
                        </p>
                      </div>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 99,
                          fontSize: 12,
                          fontWeight: 800,
                          background: "rgba(239,68,68,.12)",
                          color: "#EF4444",
                          flexShrink: 0,
                        }}>
                        {wt.mistakeRate}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Ko'p xato qilingan savollar */}
            {data.mistakes.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <p
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 10,
                  }}>
                  Ko'p xato qilingan savollar
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {data.mistakes.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        background: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: 14,
                        padding: 14,
                      }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 10,
                          marginBottom: 10,
                        }}>
                        <p
                          style={{
                            color: "#E2E8F0",
                            fontSize: 13,
                            lineHeight: 1.5,
                            fontWeight: 500,
                            flex: 1,
                          }}>
                          {m.question}
                        </p>
                        <span
                          style={{
                            padding: "3px 8px",
                            borderRadius: 8,
                            fontSize: 10,
                            fontWeight: 800,
                            background: "rgba(239,68,68,.1)",
                            color: "#EF4444",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                          }}>
                          {m.mistakeCount}x xato
                        </span>
                      </div>
                      <div
                        style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {m.choices.map((c) => {
                          const isCorrect =
                            c.order_index === m.correctAnswerIndex;
                          return (
                            <div
                              key={c.order_index}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "7px 10px",
                                borderRadius: 8,
                                background:
                                  isCorrect ?
                                    "rgba(34,197,94,.08)"
                                  : "transparent",
                                border:
                                  isCorrect ?
                                    "1px solid rgba(34,197,94,.25)"
                                  : "1px solid transparent",
                              }}>
                              {isCorrect && (
                                <CheckCircle2
                                  size={14}
                                  style={{ color: "#22C55E", flexShrink: 0 }}
                                />
                              )}
                              <span
                                style={{
                                  color: isCorrect ? "#86EFAC" : "#94A3B8",
                                  fontSize: 12,
                                }}>
                                {c.text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* So'nggi sessiyalar */}
            {data.progress.recentSessions.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 10,
                  }}>
                  <Clock size={14} style={{ color: "var(--muted)" }} />
                  <span
                    style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    So'nggi sessiyalar
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {data.progress.recentSessions.slice(0, 5).map((s) => (
                    <div
                      key={s.sessionId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "9px 12px",
                        borderRadius: 10,
                        background: "var(--card2)",
                        border: "1px solid var(--border)",
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 10,
                            fontWeight: 700,
                            background: "rgba(59,130,246,.1)",
                            color: "#60A5FA",
                          }}>
                          {MODE_LABELS[s.mode] || s.mode}
                        </span>
                        <span style={{ color: "#94A3B8", fontSize: 12 }}>
                          {s.topic?.name_oz || `${s.totalQuestions} savol`}
                        </span>
                      </div>
                      <span
                        style={{
                          color:
                            Number(s.score_percent) >= 85 ?
                              "#22C55E"
                            : "#F59E0B",
                          fontWeight: 700,
                          fontSize: 12,
                        }}>
                        {s.score_percent != null ?
                          `${Math.round(Number(s.score_percent))}%`
                        : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA: chat */}
            <button
              onClick={() => navigate("/ai-ustoz/chat")}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 14,
                background: "#3B82F6",
                border: "none",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}>
              <MessageCircle size={16} /> Ustoz AI bilan suhbatlashish
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsPage;
